import { useRef, useEffect } from 'react'
import clsx from 'clsx'
import { useKeyboardShortcut, isMac, getShortcutDisplay } from '../../hooks/useKeyboardShortcut'

interface ForecastInputProps {
  value: string
  onChange: (value: string) => void
  onSubmit: () => void
  isDisabled: boolean
  isAnalyzing: boolean
  isRunning: boolean
}

export function ForecastInput({
  value,
  onChange,
  onSubmit,
  isDisabled,
  isAnalyzing,
  isRunning,
}: ForecastInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  // Cmd/Ctrl + Enter to submit
  useKeyboardShortcut(
    [
      { key: 'Enter', modifiers: ['meta'] },
      { key: 'Enter', modifiers: ['ctrl'] },
    ],
    () => {
      if (!isDisabled && value.trim()) {
        onSubmit()
      }
    },
    { enabled: !isDisabled }
  )

  // Focus input on mount
  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  const shortcutLabel = getShortcutDisplay(isMac() ? ['meta'] : ['ctrl'], 'Enter')

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.metaKey && !e.ctrlKey) {
      e.preventDefault()
      if (!isDisabled && value.trim()) {
        onSubmit()
      }
    }
  }

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Ask any prediction question... (e.g., 'Will Bitcoin hit $100k by 2025?')"
        className="w-full px-6 py-4 bg-void-50 border border-star-dim/20 rounded-xl text-star placeholder:text-star-dim/50 focus:outline-none focus:border-signal-blue/50 focus:ring-2 focus:ring-signal-blue/20 transition-all text-lg pr-36"
        disabled={isDisabled}
      />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
        {/* Keyboard shortcut hint */}
        {!isDisabled && value.trim() && (
          <span className="text-xs text-star-dim/50 font-mono hidden sm:inline">
            {shortcutLabel}
          </span>
        )}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value.trim() || isDisabled}
          className={clsx(
            'px-6 py-2 rounded-lg font-medium transition-all',
            value.trim() && !isDisabled
              ? 'bg-signal-blue text-white hover:bg-signal-blue/80'
              : 'bg-star-dim/20 text-star-dim cursor-not-allowed'
          )}
        >
          {isAnalyzing ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="hidden sm:inline">Analyzing</span>
            </span>
          ) : isRunning ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="hidden sm:inline">Running</span>
            </span>
          ) : (
            'Predict'
          )}
        </button>
      </div>
    </div>
  )
}

// Expose ref focus method for parent components
ForecastInput.focus = (ref: React.RefObject<HTMLInputElement>) => {
  ref.current?.focus()
}
